import { apiClient } from './ApiClient'

export interface TrialStatus {
  accountStatus: string
  isTrialActive: boolean
  trialStartedAt: string
  trialEndsAt: string
  daysRemaining: number
  billingPlan: string
  monthlyAmount: number
  currency: string // ISO 4217 currency code (GBP, USD, EUR, etc.)
  hasStripeCustomer: boolean
  hasActiveSubscription: boolean
  purchaseSource?: string // "Marketplace" or "Direct"
  termsAccepted?: boolean
  termsAcceptedAt?: string
  marketingConsent?: boolean
  marketingConsentAt?: string
}

export interface UpgradeRequest {
  productId: string
  billingPlan?: string
}

export interface UpgradeResponse {
  subscriptionId: string
  accountStatus: string
  subscriptionStartedAt: string
}

export interface BillingPortalRequest {
  returnUrl: string
}

export interface BillingPortalResponse {
  portalUrl: string
}

export interface ApiResponse<T> {
  success: boolean
  data?: T
  message: string
}

export class BillingService {
  /**
   * Get current trial status and billing information
   */
  static async getTrialStatus(): Promise<TrialStatus> {
    const response = await apiClient.get<TrialStatus>('/billing/status')
    return response
  }

  /**
   * Upgrade trial to paid subscription
   */
  static async upgradeTrial(request: UpgradeRequest): Promise<ApiResponse<UpgradeResponse>> {
    const response = await apiClient.post<ApiResponse<UpgradeResponse>>('/billing/upgrade', request)
    return response
  }

  /**
   * Get billing portal URL for subscription management
   */
  static async getBillingPortal(request: BillingPortalRequest): Promise<BillingPortalResponse> {
    const response = await apiClient.post<BillingPortalResponse>('/billing/portal', request)
    return response
  }

  /**
   * Check if user is in trial period
   */
  static async isTrialActive(): Promise<boolean> {
    try {
      const data = await this.getTrialStatus()
      return data.isTrialActive === true
    } catch (error) {
      console.error('Error checking trial status:', error)
      return false
    }
  }

  /**
   * Get days remaining in trial
   */
  static async getDaysRemaining(): Promise<number> {
    try {
      const data = await this.getTrialStatus()
      return data.daysRemaining
    } catch (error) {
      console.error('Error getting days remaining:', error)
      return 0
    }
  }

  /**
   * Check if trial has expired
   */
  static async isTrialExpired(): Promise<boolean> {
    try {
      const data = await this.getTrialStatus()
      return data.accountStatus === 'expired_trial'
    } catch (error) {
      console.error('Error checking trial expiration:', error)
      return false
    }
  }

  /**
   * Handle 402 Payment Required responses
   */
  static handlePaymentRequired(error: any): boolean {
    if (error.response?.status === 402) {
      // Trial has expired - redirect to upgrade page
      const errorData = error.response.data
      if (errorData?.errorCode === 'TRIAL_EXPIRED') {
        // Store trial end date for upgrade page
        sessionStorage.setItem('trialEndedAt', errorData.trialEndedAt)
        window.location.href = '/billing/upgrade'
        return true
      }
    }
    return false
  }
}

export default BillingService